<script lang="ts">
    import { service } from "@frontend/services";
    import { GetCachedToken, me } from "@frontend/user";
    import { AddUserDTO, ApiResultOfPublicUserDTO, UserRole } from "@frontend/walletapi";
    import { groups } from "@frontend/grouplist";
    import { translate, currentLanguage } from "../Translator";
    
    export let role: UserRole;
    export let dismiss: () => void;
     
    let emptyUser: AddUserDTO = {
        Email: "",
        FirstName: "",
        LastName: "",
        Password: "",
        PhoneNumber: "",
        Role: role,
        Enabled: true, 
        Birthday : new Date(),
        Reference: "",
        CountryCode: "",
        CurrencyCode: "",
        GroupId: 0,
        Dailylimit : undefined,
        Txlimit : undefined
    };
    
    // Separate variables for form inputs to handle numeric values
    let dailyLimitValue: number | undefined = undefined;
    let txLimitValue: number | undefined = undefined;
    
    let newUser: AddUserDTO = {...emptyUser};
    let result: ApiResultOfPublicUserDTO | undefined = undefined;
    
    async function AddUser() {
        newUser.Enabled = true;
        
        // Convert numeric values to Money objects for Dailylimit and Txlimit
        if (dailyLimitValue !== undefined && dailyLimitValue !== null) {
            newUser.Dailylimit = { InCents: Math.round(Number(dailyLimitValue) * 100) };
        }
        
        if (txLimitValue !== undefined && txLimitValue !== null) {
            newUser.Txlimit = { InCents: Math.round(Number(txLimitValue) * 100) };
        }
        
        result = await service.walletapi.addUser({
            Token: GetCachedToken(),
            Data: newUser,
        });

        if (result?.Success) {
            setTimeout(dismiss, 2000);
        }
    }

    function filterGroups(group) {
        // Debug info
        console.log('Filtrando grupo:', {
            grupoId: group.Id,
            grupoNombre: group.Name,
            grupoPadre: group.ParentId,
            miGrupo: $me?.GroupId,
            miRol: $me?.Role
        });

        // Si soy Owner, puedo ver todos los grupos
        if ($me?.Role === 'Owner') {
            console.log('Owner: puede ver todos los grupos');
            return true;
        }

        // Si no hay grupo de usuario definido
        if (!$me?.GroupId) {
            console.log('No hay grupo de usuario definido');
            return false;
        }

        const myGroupId = $me.GroupId;

        // 1. Es mi grupo
        if (group.Id === myGroupId) {
            console.log('Es mi grupo:', group.Name);
            return true;
        }

        // 2. Es hijo directo de mi grupo
        if (group.ParentId === myGroupId) {
            console.log('Es hijo directo de mi grupo:', group.Name);
            return true;
        }

        // 3. Es descendiente (nieto o más profundo)
        let currentGroup = group;
        while (currentGroup?.ParentId) {
            // Debug
            console.log('Verificando grupo ancestro:', {
                grupoActual: currentGroup.Id,
                grupoPadre: currentGroup.ParentId
            });

            // Si encontramos que algún ancestro es nuestro grupo, incluimos el grupo
            if (currentGroup.ParentId === myGroupId) {
                console.log('Es descendiente de mi grupo:', group.Name);
                return true;
            }

            // Buscar el siguiente grupo padre
            currentGroup = $groups.find(g => g.Id === currentGroup?.ParentId);
        }

        console.log('No es parte de mi jerarquía:', group.Name);
        return false;
    }

    function sortGroups(a, b) {
        // Si soy Owner, ordenar jerárquicamente
        if ($me?.Role === 'Owner') {
            // Primero los grupos raíz (sin padre)
            if (!a.ParentId && b.ParentId) return -1;
            if (a.ParentId && !b.ParentId) return 1;
            
            // Luego por ID para mantener consistencia
            return Number(a.Id) - Number(b.Id);
        }
        
        // Para otros roles, mostrar primero mi grupo
        const myGroupId = $me?.GroupId;
        if (a.Id === myGroupId) return -1;
        if (b.Id === myGroupId) return 1;
        
        // Luego ordenar alfabéticamente
        return (a.Name || '').localeCompare(b.Name || '');
    }
</script>

<div class="user-form-container">
    <h2 class="ui centered dividing header">{$translate('New')} {role}</h2>

    {#if result && result.Success && result.Data?.Id}
        <div class="ui positive message">
            <div class="header">{role} {$translate('added successfully')}</div>
        </div>

        <div class="ui segment">
            <table class="receipt-table">
                <tr><td class="receipt-title">{$translate('Id')}</td><td class="receipt-value">#{result.Data.Id}</td></tr>
                <tr><td class="receipt-title">{$translate('First Name')}</td><td class="receipt-value">{result.Data.FirstName} {result.Data.LastName}</td></tr>
                <tr><td class="receipt-title">{ $translate('E-mail')}</td><td class="receipt-value">{result.Data.Email}</td></tr>
                <tr><td class="receipt-title">{ $translate('Referral')}</td><td class="receipt-value">{result.Data.Reference}</td></tr>
                <tr><td class="receipt-title">{$translate('Role')}</td><td class="receipt-value">{result.Data.Role}</td></tr>
                <tr><td class="receipt-title">{$translate('Group')}</td><td class="receipt-value">{$groups.find(g => g.Id === result?.Data?.GroupId)?.Name ?? "None"}</td></tr>
            </table>
        </div>

        <div class="button-container">
            <button class="ui primary fluid button" on:click={dismiss}>Dismiss</button>
        </div>
    {:else}
        {#if result && !result.Success}
            <div class="ui negative message">
                <div class="header">Error</div>
                <p>{result.ErrorMessage ?? `Unknown error when adding ${role}`}</p>
            </div>
        {/if}

        <form class="ui form" on:submit|preventDefault={AddUser}>
            <div class="two fields">
                <div class="field">
                    <label>{$translate('First Name')}</label>
                    <input type="text" bind:value={newUser.FirstName} placeholder="First Name" required>
                </div>
                <div class="field">
                    <label>{$translate('Last Name')}</label>
                    <input type="text" bind:value={newUser.LastName} placeholder="Last Name" required>
                </div>
            </div>

            <div class="two fields">
                <div class="field">
                    <label>{$translate('E-mail')}</label>
                    <input type="email" bind:value={newUser.Email} placeholder="Email" required>
                </div>
                <div class="field">
                    <label>{$translate('Password')}</label>
                    <input type="password" bind:value={newUser.Password} placeholder="Password" required>
                </div>
            </div>

                  <div class="field">
                    <label>{$translate('Birthday')}</label>
                    <input type="date" bind:value={newUser.Birthday} placeholder="Birthday" required>
                </div>
    
            <div class="two fields">
                <div class="field">
                    <label>{$translate('Phone Number')}</label>
                    <input type="tel" bind:value={newUser.PhoneNumber} placeholder="Phone Number" required>
                </div>
                <div class="field">
                    <label>{$translate('YOUR REFERENCE WILL BE ASSIGNED AUTOMATICALLY')}</label>
                </div>
            </div>

            <div class="field">
                <label>Group</label>
                {#if $me?.Role?.toLowerCase() === 'owner'}
                    <div class="ui info message">
                        <div class="header">{$translate('Owner Permissions')}</div>
                        <p>{$translate('As an Owner, you can assign users to any group in the system.')}</p>
                    </div>
                {:else}
                    <div class="ui info message">
                        <div class="header">{$translate('Available Groups')}</div>
                        <p>{$translate('You can assign users to your group and any subgroups under your hierarchy.')}</p>
                    </div>
                {/if}
                <select class="ui dropdown" bind:value={newUser.GroupId} required>
                    <option value="">{$translate('Select a group...')}</option>
                    {#if $me?.Role?.toLowerCase() === 'owner'}
                        {#each $groups.sort(sortGroups) as group}
                            {@const indent = group.ParentId ? '  └─ ' : ''}
                            <option value={group.Id}>
                                [{group.Id}] {indent}{group.Name}
                            </option>
                        {/each}
                    {:else}
                        {#each $groups.filter(filterGroups).sort(sortGroups) as group}
                            {@const isMyGroup = group.Id === $me?.GroupId}
                            <option value={group.Id}>
                                {#if isMyGroup}
                                    [{group.Id}] 📁 {group.Name} (Mi Grupo)
                                {:else if group.ParentId === $me?.GroupId}
                                    [{group.Id}] └─ {group.Name} (Hijo directo)
                                {:else}
                                    [{group.Id}] └─ {group.Name}
                                {/if}
                            </option>
                        {/each}
                    {/if}
                </select>
            </div>

            {#if role === 'Cashier'}
                <div class="two fields">
                    <div class="field">
                        <label>{$translate('Daily Limit')}</label>
                        <input type="number" bind:value={dailyLimitValue} placeholder="Daily transaction limit" min="0" step="0.01">
                    </div>
                    <div class="field">
                        <label>{$translate('Txlimit')}</label>
                        <input type="number" bind:value={txLimitValue} placeholder="Per transaction limit" min="0" step="0.01">
                    </div>
                </div>
            {/if}

            <div class="button-container">
                <button class="cancel-button" type="button" on:click={dismiss}>
                    {$translate('Cancel')}
                </button>
                <button class="submit-button" type="submit">
                    {$translate('Add user')} {role}
                </button>
            </div>
        </form>
    {/if}
</div>

<style>
    .user-form-container {
        max-width: 600px;
        margin: 0 auto;
        padding: 1rem;
    }

    .button-container {
        display: flex;
        justify-content: center;
        gap: 1rem;
        margin-top: 1rem;
        flex-wrap: wrap;
    }

    .cancel-button {
        background-color: #f8f9fa;
        color: #333;
        border: 1px solid #ddd;
        padding: 0.75rem 1.5rem;
        border-radius: 4px;
        font-size: 1rem;
        font-weight: 500;
        cursor: pointer;
        min-width: 120px;
        transition: all 0.2s ease;
    }

    .cancel-button:hover {
        background-color: #e9ecef;
        border-color: #adb5bd;
    }

    .submit-button {
        background-color: #21ba45;
        color: white;
        border: 1px solid #21ba45;
        padding: 0.75rem 1.5rem;
        border-radius: 4px;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        min-width: 120px;
        transition: all 0.2s ease;
    }

    .submit-button:hover {
        background-color: #16ab39;
        border-color: #16ab39;
    }

    /* Mobile responsive styles */
    @media (max-width: 768px) {
        .button-container {
            flex-direction: column;
            gap: 0.5rem;
        }

        .cancel-button,
        .submit-button {
            width: 100%;
            min-height: 44px;
            font-size: 0.9rem;
        }
    }

    .receipt-table {
        width: 100%;
        border-collapse: collapse;
    }

    .receipt-title {
        font-weight: bold;
        padding: 0.5rem;
        width: 30%;
    }

    .receipt-value {
        padding: 0.5rem;
    }

    .two.fields {
        display: flex;
        gap: 1rem;
        margin-bottom: 1rem;
    }

    .two.fields .field {
        flex: 1;
    }

    /* Responsive Design */
    @media (max-width: 768px) {
        .user-form-container {
            max-width: 100%;
            padding: 0.5rem;
            margin: 0;
        }

        .ui.form {
            font-size: 0.9em;
        }

        .ui.form input,
        .ui.form select {
            font-size: 16px !important; /* Prevents zoom on iOS */
            padding: 0.75em !important;
            min-height: 44px; /* Better touch target */
        }

        .ui.form label {
            font-size: 0.9em;
            font-weight: 600;
            margin-bottom: 0.5em;
            display: block;
        }

        /* Stack fields vertically on mobile */
        .two.fields {
            flex-direction: column;
            gap: 0.5rem;
        }

        .two.fields .field {
            flex: none;
            width: 100%;
        }

        /* Improve button layout */
        .button-container {
            flex-direction: column;
            width: 100%;
        }

        /* Improve dropdown */
        .ui.dropdown {
            min-height: 44px;
            font-size: 16px;
        }

        /* Improve messages */
        .ui.message {
            font-size: 0.85em;
            padding: 0.75em;
        }

        .ui.message .header {
            font-size: 1em;
            margin-bottom: 0.5em;
        }

        /* Improve receipt table */
        .receipt-table {
            font-size: 0.85em;
        }

        .receipt-title,
        .receipt-value {
            padding: 0.4rem;
            font-size: 0.9em;
        }

        /* Improve header */
        h2.ui.header {
            font-size: 1.3em;
            margin-bottom: 1em;
            color: #333;
            font-weight: 600;
            text-align: center;
            padding: 0.5rem 0;
            border-bottom: 2px solid #f0f0f0;
            margin-top: 0;
        }

        @media (max-width: 768px) {
            h2.ui.header {
                font-size: 1.2em;
                margin-bottom: 1.5em;
                padding: 1rem 0;
                background-color: #f8f9fa;
                border-radius: 4px;
                margin-top: 0.5rem;
            }
        }
    }

    @media (max-width: 480px) {
        .user-form-container {
            padding: 0.25rem;
        }

        .ui.form {
            font-size: 0.85em;
        }

        .ui.form input,
        .ui.form select {
            font-size: 16px !important;
            padding: 0.8em !important;
        }

        .ui.form label {
            font-size: 0.85em;
        }

        /* Stack all fields vertically for very small screens */
        .ui.form .field {
            margin-bottom: 1.2em;
        }



        /* Improve messages */
        .ui.message {
            font-size: 0.8em;
            padding: 0.6em;
        }

        /* Improve receipt table */
        .receipt-table {
            font-size: 0.8em;
        }

        .receipt-title {
            width: 35%;
        }
    }

    /* Tablet styles */
    @media (min-width: 769px) and (max-width: 1024px) {
        .user-form-container {
            max-width: 90%;
            padding: 1.5rem;
        }

        .ui.form {
            font-size: 0.95em;
        }

        .ui.form input,
        .ui.form select {
            font-size: 16px !important;
            padding: 0.7em !important;
        }

        .two.fields {
            gap: 1.5rem;
        }
    }

    /* Improve focus states for accessibility */
    .ui.form input:focus,
    .ui.form select:focus {
        border-color: #2185d0 !important;
        box-shadow: 0 0 0 2px rgba(33, 133, 208, 0.2) !important;
    }


</style>
